import el from "@cypherpotato/el"
import { formatFloat, formatNumber } from "../ui/format";
import { Icon } from "./icon";
import { ToggleButton } from "./button";

export function GatewayPipeline(gatewayData) {
    if (!gatewayData) {
        return el(".gateway-pipeline")
    }

    const { parameters } = gatewayData;
    const hasCollections = !!parameters.knowledgeCollections?.length;
    let dragState = null;

    const handleWindowMouseMove = (event) => {
        if (!dragState) {
            return;
        }

        const deltaX = event.clientX - dragState.lastClientX;
        dragState.container.scrollLeft -= deltaX;
        dragState.lastClientX = event.clientX;
    };

    const endDrag = () => {
        if (!dragState) {
            return;
        }

        dragState.container.classList.remove("is-dragging");
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("mouseup", handleWindowMouseUp);
        dragState = null;
    };

    const handleWindowMouseUp = (event) => {
        if (event.button !== 0) {
            return;
        }

        endDrag();
    };

    const handleMouseDown = (event) => {
        if (event.button !== 0) {
            return;
        }

        const interactiveElement = event.target.closest("button, a, input, textarea, select, [data-no-drag]");
        if (interactiveElement) {
            return;
        }

        dragState = {
            container: event.currentTarget,
            lastClientX: event.clientX,
        };

        dragState.container.classList.add("is-dragging");
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);
        event.preventDefault();
    };

    return el(".gateway-pipeline-wrapper",
        el(".gateway-pipeline-toolbar",
            ToggleButton(Icon("eye-off-line"), "Hide disabled steps", {
                onToggle(event) {
                    event.target.closest(".gateway-pipeline-wrapper").classList.toggle("hide-disabled-steps", event.value);
                }
            })
        ),
        el(".gateway-pipeline",
            {
                onMouseDown: handleMouseDown,
                onMouseUp: handleWindowMouseUp,
                onMouseLeave(event) {
                    if (!dragState) {
                        return;
                    }

                    if (!event.buttons) {
                        endDrag();
                    }
                },
            },
            el(".pipeline-column",
                el(".column-title", "Input"),
                el(".pipeline-step.hidden-step",
                    { dataState: true }
                ),
            ),

            el(".pipeline-column",
                el(".column-title", Icon("box-3-line"), "Inference"),
                el(".pipeline-step",
                    { dataState: !!parameters.baseAddress },
                    el(".step-title", "BYOK endpoint")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.modelName },
                    el(".step-title", parameters.modelName || "no-model")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.apiKey },
                    el(".step-title", "API key")
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("database-2-line"), "RAG"),
                el(".pipeline-step",
                    { dataState: hasCollections },
                    el(".step-title", "Collections"),
                    hasCollections && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.knowledgeCollections.length)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.queryStrategy && hasCollections },
                    el(".step-title", "Query strategy"),
                    !!parameters.queryStrategy && hasCollections && el.fragment(
                        el(".step-param", "Strategy", el("span", parameters.queryStrategy))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeBaseMaximumResults != null && hasCollections },
                    el(".step-title", "Maximum results"),
                    parameters.knowledgeBaseMaximumResults != null && hasCollections && el.fragment(
                        el(".step-param", "Value", el("span", formatNumber(parameters.knowledgeBaseMaximumResults)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeBaseMinimumScore != null && hasCollections },
                    el(".step-title", "Minimum score"),
                    parameters.knowledgeBaseMinimumScore != null && hasCollections && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.knowledgeBaseMinimumScore)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeUseReferences && hasCollections },
                    el(".step-title", "References")
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeUseMetaDescriptions && hasCollections },
                    el(".step-title", "Collection context")
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("equalizer-3-line"), "Inference options"),
                el(".pipeline-step",
                    { dataState: parameters.temperature != null },
                    el(".step-title", "Temperature"),
                    parameters.temperature != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.temperature)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.topP != null },
                    el(".step-title", "Top P"),
                    parameters.topP != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.topP)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.presencePenalty != null },
                    el(".step-title", "Presence penalty"),
                    parameters.presencePenalty != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.presencePenalty)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.frequencyPenalty != null },
                    el(".step-title", "Frequency penalty"),
                    parameters.frequencyPenalty != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.frequencyPenalty)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.reasoningEffort != null },
                    el(".step-title", "Reasoning effort"),
                    parameters.reasoningEffort != null && el.fragment(
                        el(".step-param", "Value", el("span", parameters.reasoningEffort))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.maxCompletionTokens != null },
                    el(".step-title", "Max completion tokens"),
                    parameters.maxCompletionTokens != null && el.fragment(
                        el(".step-param", "Value", el("span", formatNumber(parameters.maxCompletionTokens)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.stop },
                    el(".step-title", "Stop sequence")
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("presentation-line"), "Instructions"),
                el(".pipeline-step",
                    { dataState: !!parameters.systemInstruction },
                    el(".step-title", "System instruction")
                )
            ),
            el(".pipeline-column",
                el(".column-title", "Output"),
                el(".pipeline-step.hidden-step",
                    { dataState: true }
                ),
            ),
        )
    )
}
